import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { useSelector } from "react-redux";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import FormControl from "@mui/material/FormControl";
import {
  getCustomerBanDurationOptions,
  getDefaultCustomerBanDuration,
} from "../../../../utils/customerMappers";
import {
  peopleSelectControlSx,
  peopleSelectMenuProps,
  peopleSelectSx,
} from "../peopleSelectStyles";

function BanWarningIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 13.1199V9.37988"
        stroke="#FF4D4F"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.999 16.125C11.861 16.125 11.749 16.237 11.75 16.375C11.75 16.513 11.862 16.625 12 16.625C12.138 16.625 12.25 16.513 12.25 16.375C12.25 16.237 12.138 16.125 11.999 16.125"
        stroke="#FF4D4F"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.029 4.13894L21.688 17.5429C22.578 19.1009 21.453 21.0399 19.659 21.0399H4.34101C2.54601 21.0399 1.42101 19.1009 2.31201 17.5429L9.97101 4.13894C10.868 2.56794 13.132 2.56794 14.029 4.13894Z"
        stroke="#FF4D4F"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function CustomerBanModal({ open, customer, onClose, onConfirm }) {
  const roleName = useSelector((state) => state.loginToken?.response?.user?.role_name);
  const durationOptions = useMemo(
    () => getCustomerBanDurationOptions(roleName),
    [roleName]
  );
  const defaultDuration = useMemo(
    () => getDefaultCustomerBanDuration(roleName),
    [roleName]
  );
  const [duration, setDuration] = useState(defaultDuration);

  useEffect(() => {
    if (open) setDuration(defaultDuration);
  }, [open, defaultDuration]);

  const handleClose = () => {
    setDuration(defaultDuration);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(duration);
    setDuration(defaultDuration);
  };

  if (!customer) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 408,
          maxWidth: "95vw",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
        },
      }}
    >
      <Box sx={{ p: "24px", position: "relative" }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "#FFF1F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <BanWarningIcon />
        </Box>

        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "20px",
            mb: 1,
          }}
        >
          Ban Customer
        </Typography>

        <Typography
          sx={{
            color: "#556575",
            fontFamily: "Inter",
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "22px",
            mb: 2.5,
          }}
        >
          You are about to ban{" "}
          <Box component="span" sx={{ color: "#061522", fontWeight: 400 }}>
            {customer.name}
          </Box>{" "}
          from Live Chat.
        </Typography>

        <Typography
          sx={{
            color: "#405261",
            fontFamily: "Inter",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: "18px",
            mb: 0.75,
          }}
        >
          Ban Duration
        </Typography>

        <FormControl
          fullWidth
          size="small"
          sx={peopleSelectControlSx({ width: 360, maxWidth: "100%" })}
        >
          <Select
            fullWidth
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            IconComponent={Dropdown}
            MenuProps={peopleSelectMenuProps}
            sx={peopleSelectSx}
          >
          {durationOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: "Inter", fontSize: 14 }}>
              {opt.label}
            </MenuItem>
          ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              background: "#FF4D4F",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: "#E63E40" },
            }}
          >
            Ban Customer
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
